import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calculator, ShieldCheck, PlusCircle, ArrowRight, Coins, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export default function HomeSidebar() {
  const { t } = useLanguage();

  return (
    <aside className="space-y-4">
      {/* Widget 1: Create Item CTA Card */}
      <motion.div 
        whileHover={{ y: -2 }}
        className="rounded-3xl bg-card/60 backdrop-blur-xl border border-border/40 p-5 shadow-sm space-y-3 text-foreground"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shadow-2xs">
            <PlusCircle size={20} />
          </div>
          <div>
            <h3 className="text-sm font-black text-foreground">{t('haveSomethingToTrade', 'רוצה להחליף משהו?')}</h3>
            <p className="text-[11px] text-muted-foreground font-medium">{t('postItemDesc', 'פרסם פריט בחינם וקבל הצעות החלפה')}</p>
          </div>
        </div>
        <Link
          to="/create"
          className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-2xl bg-primary text-primary-content font-bold text-xs uppercase tracking-wider hover:bg-primary/90 transition-all shadow-2xs active:scale-95"
        >
          <span>{t('postItemNow', 'פרסם פריט עכשיו')}</span>
          <ArrowRight size={15} />
        </Link>
      </motion.div>

      {/* Widget 2: Swap Value Estimator / Calculator */}
      <div className="rounded-3xl bg-card/60 backdrop-blur-xl border border-border/40 p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-2xs">
            <Calculator size={18} />
          </div>
          <div>
            <h4 className="text-sm font-black text-foreground">{t('valueEstimator', 'מחשבון שווי מוערך')}</h4>
            <p className="text-[11px] text-muted-foreground font-medium">{t('valueEstimatorDesc', 'שנבדוק את שווי הפריט שלך?')}</p>
          </div>
        </div>
        <div className="pt-2 border-t border-border/30">
          <Link
            to="/create"
            className="flex items-center justify-between p-2.5 rounded-2xl bg-muted/30 hover:bg-muted/60 transition-colors border border-border/30 text-xs font-bold text-foreground group"
          >
            <span>{t('calculateSwapValue', 'בדוק שווי פריט לפני החלפה')}</span>
            <ArrowRight size={14} className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-all" />
          </Link>
        </div>
      </div>

      {/* Widget 3: Coin Boost Store Widget */}
      <div className="rounded-3xl bg-card/60 backdrop-blur-xl border border-border/40 p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-2xl bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-2xs">
            <Coins size={18} />
          </div>
          <div>
            <h4 className="text-sm font-black text-foreground">{t('boostYourItem', 'הבלט את המודעה שלך')}</h4>
            <p className="text-[11px] text-muted-foreground font-medium">{t('boostDesc', 'קבל עד פי 5 פניות עם מטבעות')}</p>
          </div>
        </div>
        <Link
          to="/coins"
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-bold text-xs border border-amber-500/20 transition-all active:scale-95 shadow-2xs"
        >
          <Sparkles size={14} />
          <span>{t('buyCoinsStore', 'חנות המטבעות וההבלטות')}</span>
        </Link>
      </div>

      {/* Widget 4: Safe Trading Guide */}
      <div className="rounded-3xl bg-card/60 backdrop-blur-xl border border-border/40 p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-2xs">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h4 className="text-sm font-black text-foreground">{t('safeTradingGuide', 'כל מה שצריך לדעת')}</h4>
            <p className="text-[11px] text-muted-foreground font-medium">{t('safeTradeSub', 'מדריך החלפה בטוחה ומהירה')}</p>
          </div>
        </div>
        <div className="space-y-1.5 pt-2 border-t border-border/30 text-xs font-medium text-muted-foreground">
          <Link to="/faq" className="flex items-center justify-between py-1 hover:text-primary transition-colors">
            <span>• {t('howToTradeSafely', 'איך מבצעים החלפה בטוחה?')}</span>
            <ArrowRight size={12} />
          </Link>
          <Link to="/faq" className="flex items-center justify-between py-1 hover:text-primary transition-colors">
            <span>• {t('cashFlexibilityGuide', 'איך עובד איזון כספי?')}</span>
            <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </aside>
  );
}
